import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Tabs,
  Tab,
  Button,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import GeneCard from '../components/GeneCard';
import AssociationTable from '../components/AssociationTable';
import ForestPlot from '../components/ForestPlot';
import GWASAssociationTable from '../components/GWASAssociationTable';
import GWASForestPlot from '../components/GWASForestPlot';
import PleiotropySummary from '../components/PleiotropySummary';
import { getGeneDetails, getExportUrl } from '../services/api';

const GeneDetailPage = () => {
  const { symbol } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const initialTab = parseInt(searchParams.get('tab') || '0', 10);
  const [tabValue, setTabValue] = useState(isNaN(initialTab) ? 0 : initialTab);

  const handleTabChange = (e, newValue) => {
    setTabValue(newValue);
    const params = new URLSearchParams(searchParams);
    if (newValue === 0) {
      params.delete('tab');
    } else {
      params.set('tab', newValue);
    }
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    const fetchGeneDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getGeneDetails(symbol);
        setData(response);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load gene details. The gene may not exist in our database.');
      } finally {
        setLoading(false);
      }
    };

    if (symbol) {
      document.title = `${symbol} - Genetics Database`;
      fetchGeneDetails();
    }
  }, [symbol]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!data) return null;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <GeneCard gene={data.gene} constraint={data.constraint} />
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          href={getExportUrl(symbol)}
          target="_blank"
          sx={{ ml: 2, mt: 1, whiteSpace: 'nowrap' }}
        >
          Export CSV
        </Button>
      </Box>

      <Paper sx={{ mt: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Associations Table" />
          <Tab label="Forest Plot" />
          <Tab label="GWAS Associations" />
          <Tab label="GWAS Forest Plot" />
          <Tab label="Pleiotropy" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tabValue === 0 && <AssociationTable associations={data.associations} />}
          {tabValue === 1 && <ForestPlot associations={data.associations} />}
          {tabValue === 2 && <GWASAssociationTable gwasData={data.gwas} />}
          {tabValue === 3 && <GWASForestPlot gwasData={data.gwas} />}
          {tabValue === 4 && <PleiotropySummary geneSymbol={symbol} />}
        </Box>
      </Paper>
    </Container>
  );
};

export default GeneDetailPage;
